import React, { useState } from 'react';
import BrandLockup from '../components/BrandLockup';
import PageLoader from '../components/PageLoader';
import { useActor } from './ActorContext';
import './ActorGate.css';


function WorkspaceCard({ title, description, children }) {
  return (
    <div className="workspace-gate">
      <div className="workspace-gate__card">
        <BrandLockup className="brand-lockup--auth" title="GravviSoft" />
        <div className="workspace-gate__heading">
          <h1>{title}</h1>
          <p>{description}</p>
        </div>
        {children}
      </div>
    </div>
  );
}

function InvitationAcceptance({ token }) {
  const { acceptInvitation } = useActor();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const accept = async () => {
    setSubmitting(true);
    setError('');
    try {
      await acceptInvitation(token);
      window.history.replaceState({}, '', window.location.pathname);
    } catch (requestError) {
      setError(requestError?.message || 'Could not accept this invitation.');
      setSubmitting(false);
    }
  };

  return (
    <WorkspaceCard
      title="Join your workspace"
      description="Your account is ready. Accept the invitation to activate the role assigned to you."
    >
      {error && <p className="workspace-gate__error" role="alert">{error}</p>}
      <button type="button" className="workspace-gate__retry" onClick={accept} disabled={submitting}>
        {submitting ? 'Joining workspace…' : 'Accept invitation'}
      </button>
    </WorkspaceCard>
  );
}

function OrganizationSelection() {
  const { actor, selectOrganization } = useActor();
  const [submittingId, setSubmittingId] = useState('');
  const [error, setError] = useState('');

  const choose = async (organizationId) => {
    setSubmittingId(organizationId);
    setError('');
    try {
      await selectOrganization(organizationId);
    } catch (requestError) {
      setError(requestError?.message || 'Could not open that workspace.');
      setSubmittingId('');
    }
  };

  return (
    <WorkspaceCard title="Choose a workspace" description="Select the organization you want to work in right now.">
      <div className="workspace-gate__choices">
        {actor.available_organizations.map((organization) => (
          <button
            type="button"
            key={organization.organization_id}
            onClick={() => choose(organization.organization_id)}
            disabled={Boolean(submittingId)}
          >
            <strong>{organization.name}</strong>
            <span>{organization.slug}</span>
          </button>
        ))}
      </div>
      {error && <p className="workspace-gate__error" role="alert">{error}</p>}
    </WorkspaceCard>
  );
}

export default function ActorGate({ children }) {
  const { actor, loading, error, refreshActor } = useActor();
  const invitationToken = new URLSearchParams(window.location.search).get('invitation');

  if (loading) return <PageLoader label="Loading your workspace" />;
  if (error) {
    return (
      <WorkspaceCard title="Workspace unavailable" description={error}>
        <button type="button" className="workspace-gate__retry" onClick={() => refreshActor().catch(() => {})}>
          Try again
        </button>
      </WorkspaceCard>
    );
  }
  if (!actor) return null;
  if (invitationToken) {
    return <InvitationAcceptance token={invitationToken} />;
  }
  // Legacy investor accounts must keep their existing dashboard access until
  // organization creation is folded into an explicitly approved signup and
  // account-migration flow.
  if (actor.available_organizations.length === 0) return children;
  if (actor.organization_selection_required) return <OrganizationSelection />;
  return children;
}
